import classNames from "classnames";
import React, { useEffect, useState } from "react";
import * as Icons from "Components/Icons";
import { RatingClient } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { UncontrolledTooltip } from "Components/ReactstrapSlim";
import uniqueId from "lodash/uniqueId";

interface RatingProps {
   className?: string;
   stars: number | undefined;
   hideNumber?: boolean;
   onClick?: React.MouseEventHandler<HTMLDivElement>;
}
export const Rating: React.FC<RatingProps> = function ({ stars, className, hideNumber, onClick }) {
   return (
      <div className={classNames("d-flex align-items-center", className)} onClick={onClick}>
         {
            stars == null
               ? <>&nbsp;</>
               : <>
                  {[...Array(5)].map((x, i) => <Icons.Star key={i} className={classNames({ "tc-icon-star": i < Math.round(stars), "tc-icon-active": i >= Math.round(stars) })} />)}
                  {/* <Icons.Star className="d-block d-sm-none tc-icon-star" /> */}
                  {!hideNumber && <span className="lbl1b ml-px4">{stars.toFixed(1)}</span>}
               </>
         }
      </div>
   );
}

interface RatingAsyncProps {
   className?: string;
   venueId: Guid;
   workspaceId?: Guid;
   lazy?: boolean;
}
export const RatingAsync: React.FC<RatingAsyncProps> = function ({ venueId, workspaceId, className }) {
   const [stars, setStars] = useState<number | null>(null);
   const ratingClient = useApiClient(RatingClient);

   useEffect(() => {
      const abortController = new AbortController();
      ratingClient.average(venueId, workspaceId, abortController.signal)
         .then(data => setStars(data.average));
      return function cleanup() {
         abortController.abort();
      }
   }, [venueId, workspaceId]);

   return stars && stars >= 1
      ? <Rating stars={stars} className={className} />
      : null;
}


export type RatingBadgeTheme = "gray" | "yellow" | "transparent";

export interface RatingBadgeProps {
   averageRating: number | undefined;
   ratingCount: number | undefined;
   theme?: RatingBadgeTheme,
   className?: string;
}

const RatingBadgeImpl: React.FC<RatingBadgeProps> = function ({ averageRating, ratingCount, className, theme = "gray" }) {
   const [id] = useState(() => uniqueId("rating-"));
   const [fixedRating] = useState(() => (averageRating ?? 0).toFixed(1));

   return (<div className={classNames("rating-badge", className, `rating-badge-${theme}`)} id={id}>
      <Icons.Star className="tc-icon-star" />
      <span className="lbl1b ml-px8">{fixedRating}</span>
      <UncontrolledTooltip placement="top" target={id} fade={false}>
         This location is rated {fixedRating} by {ratingCount && ratingCount > 1 ? `${ratingCount} people` : "1 person"}
      </UncontrolledTooltip >
   </div>);
}

export const RatingBadge: React.FC<RatingBadgeProps> = props =>
   !props.averageRating || props.averageRating < 1
      ? null
      : <RatingBadgeImpl {...props} />